import React from 'react';
import { GraduationCap, Briefcase, BookOpen } from 'lucide-react';
import { DonutChart } from './DonutChart';
import { ProgressBar } from './ProgressBar';
import { StatCard } from './StatCard';
import { StatSection } from './StatSection';

interface AcademicStatsProps {
  academicStats: {
    distribucionNivelEstudios: Array<{ nivel: string; count: number }>;
    topProfesiones: Array<{ profesion: string; count: number }>;
    conProfesion: number;
    sinProfesion: number;
  };
  totalConDatosComplementarios: number;
}

export const AcademicStats: React.FC<AcademicStatsProps> = ({
  academicStats,
  totalConDatosComplementarios
}) => {
  const { distribucionNivelEstudios, topProfesiones, conProfesion, sinProfesion } = academicStats;

  // Colores para niveles de estudio
  const nivelColores = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

  return (
    <div className="space-y-6">
      {/* Cards de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Con Profesión"
          value={conProfesion}
          icon={<Briefcase className="w-6 h-6" />}
          color="blue"
          subtitle={`${((conProfesion / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(1)}% con datos`}
        />
        <StatCard
          title="Sin Profesión"
          value={sinProfesion}
          icon={<BookOpen className="w-6 h-6" />}
          color="gray"
          subtitle={`${((sinProfesion / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(1)}% con datos`}
        />
        <StatCard
          title="Niveles de Estudio"
          value={distribucionNivelEstudios.length}
          icon={<GraduationCap className="w-6 h-6" />}
          color="green"
          subtitle="Diferentes niveles registrados"
        />
      </div>

      {/* Distribución por nivel de estudios y profesiones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nivel de estudios */}
        {distribucionNivelEstudios.length > 0 && (
          <StatSection title="Distribución por Nivel de Estudios" icon={GraduationCap} iconColor="text-green-600">
            <div className="space-y-3">
              {distribucionNivelEstudios.map((nivel, index) => (
                <ProgressBar
                  key={index}
                  label={nivel.nivel}
                  value={nivel.count}
                  total={totalConDatosComplementarios || 1}
                  color={nivelColores[index % nivelColores.length]}
                />
              ))}
            </div>
          </StatSection>
        )}

        {/* Profesiones */}
        <div className="space-y-6">
          {topProfesiones.length > 0 && (
            <StatSection title="Top Profesiones" icon={Briefcase} iconColor="text-blue-600">
              <div className="space-y-3">
                {topProfesiones.slice(0, 5).map((prof, index) => (
                  <ProgressBar
                    key={index}
                    label={prof.profesion}
                    value={prof.count}
                    total={conProfesion || 1}
                    color={index === 0 ? '#3b82f6' : index === 1 ? '#10b981' : index === 2 ? '#f59e0b' : '#8b5cf6'}
                  />
                ))}
              </div>
            </StatSection>
          )}

          {/* Distribución con/sin profesión */}
          <StatSection title="Distribución Profesional" icon={BookOpen} iconColor="text-indigo-600">
            <div className="flex flex-col items-center">
              <DonutChart
                data={[
                  { label: 'Con profesión', value: conProfesion, color: '#3b82f6' },
                  { label: 'Sin profesión', value: sinProfesion, color: '#94a3b8' }
                ]}
                size={120}
                centerText={`${((conProfesion / Math.max(totalConDatosComplementarios, 1)) * 100).toFixed(0)}%`}
                centerSubtext="Con prof."
              />
              <div className="flex justify-center space-x-4 mt-4 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  <span className="text-gray-600">Con: {conProfesion}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                  <span className="text-gray-600">Sin: {sinProfesion}</span>
                </div>
              </div>
            </div>
          </StatSection>
        </div>
      </div>
    </div>
  );
};

export default AcademicStats;
